import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, InputGroup, Modal, Row, Spinner } from 'react-bootstrap';
import {
    createCollaboratorPortalAppointment,
    getAvailableSlots,
    getCollaboratorPortalBookingBranches,
    getCollaboratorPortalBookingServices,
    searchCollaboratorPortalCustomers
} from '../../../../services/PublicBookingService';
import { formatCpf } from '../../../../utils/PublicBookingIdentifier';
import { formatPhoneNumber } from '../../../../utils/FormatUtils';
import { isOnVacationOn, vacationPeriodLabel, vacationReturnLabel } from '../../../../utils/VacationUtils';
import './CollaboratorQuickBooking.css';

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const pad = (value) => String(value).padStart(2, '0');

/** yyyy-MM-dd no fuso local. */
const toDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** HH:mm no fuso local. */
const toTimeKey = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

/** Extrai o HH:mm de um horário vindo da API ("09:00", "09:00:00" ou "2026-10-01T09:00:00"). */
const slotTime = (slot) => {
    const match = String(slot?.startTime || slot?.time || '').match(/(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : null;
};

/**
 * Ponto de partida quando aberto pelo botão "+": o próximo horário redondo de meia em meia
 * hora, para não sugerir um horário que o servidor recusaria por já ter passado.
 */
const nextRoundedStart = () => {
    const date = new Date();
    date.setSeconds(0, 0);
    date.setMinutes(date.getMinutes() <= 30 ? 30 : 60);
    return date;
};

/** Mesmos períodos da etapa de horário do site de agendamento. */
const PERIODS = [
    { key: 'morning', label: 'Manhã', icon: 'fa-sun', matches: (hour) => hour < 12 },
    { key: 'afternoon', label: 'Tarde', icon: 'fa-cloud-sun', matches: (hour) => hour >= 12 && hour < 18 },
    { key: 'evening', label: 'Noite', icon: 'fa-moon', matches: (hour) => hour >= 18 }
];

/**
 * "Sexta-feira, 02/10 às 10:30". O dia da semana começa com maiúscula, padrão das telas do
 * sistema.
 */
const formatSelection = (dateKey, timeKey) => {
    const day = new Date(`${dateKey}T12:00:00`);
    const weekday = day.toLocaleDateString('pt-BR', { weekday: 'long' });
    const dayMonth = day.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${dayMonth} às ${timeKey}`;
};

const emptyNewCustomer = { name: '', phone: '', document: '', email: '' };

const getInitials = (name) => (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/**
 * Agendamento rápido do colaborador no portal.
 *
 * Abre pelo clique num horário da agenda (já com dia e hora) ou pelo botão "+" (próximo
 * horário redondo). O colaborador não é escolhido: o servidor usa o dono do token. As regras
 * de expediente, conflito, bloqueio e plano são as mesmas da recepção e voltam como
 * mensagem do servidor quando recusam o horário.
 *
 * Os componentes seguem o site de agendamento do cliente (cards de serviço com check,
 * horários por período), para o colaborador reconhecer o mesmo fluxo. O dia é um campo de
 * data simples, que abre no dia atual.
 */
const CollaboratorQuickBooking = ({ open, initialStart, portalToken, companySlug, employee, onClose, onCreated }) => {
    const employeeId = employee?.id;
    const [branches, setBranches] = useState([]);
    const [branchId, setBranchId] = useState('');
    const [services, setServices] = useState([]);
    const [serviceIds, setServiceIds] = useState([]);
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [freeTimes, setFreeTimes] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    // Chave (unidade|dia|duração) da última lista de horários recebida. A escolha automática
    // do horário só roda depois que a lista do dia atual chegou; antes disso a lista vazia do
    // carregamento apagaria o horário que veio do clique na grade.
    const [slotsKey, setSlotsKey] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [customerMode, setCustomerMode] = useState('search');
    const [newCustomer, setNewCustomer] = useState(emptyNewCustomer);
    const [term, setTerm] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [notes, setNotes] = useState('');
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Motivo que impede o agendamento antes mesmo do formulário (recurso desligado, cadastro
    // sem liberação para atender, sem unidade ativa). Com ele preenchido, o modal mostra só o
    // aviso, e não um formulário que seria recusado no fim.
    const [unavailable, setUnavailable] = useState(null);
    const searchTimer = useRef(null);

    // Cada abertura começa limpa, com o dia e a hora de onde o colaborador clicou.
    useEffect(() => {
        if (!open) return;
        const start = initialStart || nextRoundedStart();
        setDate(toDateKey(start));
        setTime(toTimeKey(start));
        setServiceIds([]);
        setCustomer(null);
        setCustomerMode('search');
        setNewCustomer(emptyNewCustomer);
        setTerm('');
        setResults([]);
        setNotes('');
        setError(null);
        setUnavailable(null);
    }, [open, initialStart]);

    useEffect(() => {
        if (!open || !portalToken) return undefined;
        let active = true;
        setLoadingOptions(true);
        getCollaboratorPortalBookingBranches(portalToken)
            .then((response) => {
                if (!active) return;
                const list = response.data || [];
                setBranches(list);
                // Com uma unidade só não há o que perguntar; com várias, ele escolhe.
                setBranchId(list.length === 1 ? String(list[0].id) : '');
                if (list.length === 0) {
                    setUnavailable('Você ainda não está vinculado a nenhuma unidade. Fale com a recepção.');
                }
            })
            // As recusas deste ponto são do cadastro ou da empresa, e a mensagem do servidor já
            // explica o motivo; ela vira o aviso do modal.
            .catch((requestError) => {
                if (active) {
                    setUnavailable(requestError?.response?.data?.message
                        || 'Não foi possível abrir o agendamento agora. Tente novamente em instantes.');
                }
            })
            .finally(() => {
                if (active) setLoadingOptions(false);
            });
        return () => { active = false; };
    }, [open, portalToken]);

    useEffect(() => {
        if (!open || !portalToken || !branchId) {
            setServices([]);
            return undefined;
        }
        let active = true;
        getCollaboratorPortalBookingServices(portalToken, branchId)
            .then((response) => {
                if (!active) return;
                const list = response.data || [];
                setServices(list);
                // Troca de unidade: fica só o que a nova unidade também oferece.
                setServiceIds((current) => current.filter((id) => list.some((service) => service.id === id)));
            })
            .catch(() => {
                if (active) setServices([]);
            });
        return () => { active = false; };
    }, [open, portalToken, branchId]);

    const selectedServices = useMemo(
        () => serviceIds.map((id) => services.find((service) => service.id === id)).filter(Boolean),
        [serviceIds, services]
    );
    const totalDuration = selectedServices.reduce((sum, service) => sum + (service.duration || 0), 0);
    const totalPrice = selectedServices.reduce((sum, service) => sum + (service.price || 0), 0);

    const currentSlotsKey = `${branchId}|${date}|${totalDuration}`;

    // Horários livres do dia. O horário só é escolhido por esta lista, a mesma regra do site:
    // ela já respeita expediente, almoço, férias, bloqueios e agenda ocupada.
    useEffect(() => {
        if (!open || !companySlug || !branchId || !employeeId || !date) {
            setFreeTimes([]);
            return undefined;
        }
        let active = true;
        const requestKey = `${branchId}|${date}|${totalDuration}`;
        setSlotsLoading(true);
        getAvailableSlots(companySlug, branchId, employeeId, date, totalDuration || null)
            .then((response) => {
                if (!active) return;
                const slots = Array.isArray(response.data) ? response.data : [];
                setFreeTimes(slots.filter((slot) => slot?.available).map(slotTime).filter(Boolean));
            })
            .catch(() => {
                if (active) setFreeTimes([]);
            })
            .finally(() => {
                if (active) {
                    setSlotsLoading(false);
                    setSlotsKey(requestKey);
                }
            });
        return () => { active = false; };
    }, [open, companySlug, branchId, employeeId, date, totalDuration]);

    // Mantém o horário escolhido coerente com a lista do dia. Se ele está na lista, fica. Se
    // não está (clique na grade num minuto que a lista não tem, troca de dia ou serviço mais
    // longo), passa para o próximo horário livre depois dele; sem nenhum, fica sem horário.
    useEffect(() => {
        if (slotsLoading || slotsKey !== currentSlotsKey) return;
        if (time && freeTimes.includes(time)) return;
        const preferred = time || '00:00';
        setTime(freeTimes.find((item) => item >= preferred) || '');
    }, [slotsLoading, slotsKey, currentSlotsKey, freeTimes, time]);

    // Busca com espera curta entre as teclas, para não consultar a cada letra.
    useEffect(() => {
        clearTimeout(searchTimer.current);
        const normalized = term.trim();
        if (!open || customerMode !== 'search' || customer || normalized.length < 3) {
            setResults([]);
            setSearching(false);
            return undefined;
        }
        setSearching(true);
        searchTimer.current = setTimeout(() => {
            searchCollaboratorPortalCustomers(portalToken, normalized)
                .then((response) => setResults(response.data || []))
                .catch(() => setResults([]))
                .finally(() => setSearching(false));
        }, 350);
        return () => clearTimeout(searchTimer.current);
    }, [term, open, customerMode, customer, portalToken]);

    const periods = useMemo(() => PERIODS
        .map((period) => ({
            ...period,
            times: freeTimes.filter((item) => period.matches(Number(item.slice(0, 2))))
        }))
        .filter((period) => period.times.length > 0), [freeTimes]);

    const toggleService = (id) => {
        setServiceIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
    };

    const startNewCustomer = () => {
        setCustomer(null);
        setCustomerMode('new');
        // Aproveita o que ele já digitou na busca: nome ou telefone.
        const typed = term.trim();
        setNewCustomer(/\d{4,}/.test(typed)
            ? { ...emptyNewCustomer, phone: typed }
            : { ...emptyNewCustomer, name: typed });
    };

    const validate = () => {
        if (branches.length > 1 && !branchId) return 'Escolha a unidade do atendimento.';
        if (customerMode === 'search' && !customer) return 'Escolha o cliente ou cadastre um cliente novo.';
        if (customerMode === 'new') {
            if (!newCustomer.name.trim()) return 'Informe o nome do cliente.';
            if (newCustomer.phone.replace(/\D/g, '').length < 10) return 'Informe o telefone do cliente com DDD.';
            // Ao menos um dos dois: é por CPF ou e-mail que o cliente acha o horário no site e
            // em "Meus agendamentos". O servidor aplica a mesma regra.
            if (!newCustomer.document.replace(/\D/g, '') && !newCustomer.email.trim()) {
                return 'Informe o CPF ou o e-mail do cliente.';
            }
            if (newCustomer.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCustomer.email.trim())) {
                return 'Informe um e-mail válido.';
            }
        }
        if (serviceIds.length === 0) return 'Escolha ao menos um serviço.';
        if (!date) return 'Escolha o dia.';
        if (!time) return 'Escolha um dos horários livres.';
        if (new Date(`${date}T${time}:00`) < new Date()) return 'Escolha um horário futuro.';
        return null;
    };

    const submit = () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        setSaving(true);
        setError(null);
        createCollaboratorPortalAppointment(portalToken, {
            branchId: branchId ? Number(branchId) : null,
            customerId: customerMode === 'search' ? customer?.id : null,
            customerName: customerMode === 'new' ? newCustomer.name.trim() : null,
            customerPhone: customerMode === 'new' ? newCustomer.phone : null,
            customerDocument: customerMode === 'new' ? (newCustomer.document || null) : null,
            customerEmail: customerMode === 'new' ? (newCustomer.email.trim() || null) : null,
            serviceIds,
            scheduledDate: `${date}T${time}:00`,
            notes: notes.trim() || null
        })
            .then((response) => onCreated?.(response.data))
            // A mensagem do servidor explica o motivo real (fora do expediente, horário ocupado,
            // bloqueado, não atende o plano do cliente), então ela vai direto para a tela.
            .catch((requestError) => setError(
                requestError?.response?.data?.message || 'Não foi possível agendar este horário.'))
            .finally(() => setSaving(false));
    };

    const todayKey = toDateKey(new Date());
    // Em férias o servidor devolve o dia sem horários e recusaria qualquer um digitado. Sem
    // este aviso o modal dizia "nenhum horário livre", como se a agenda estivesse cheia.
    const onVacation = Boolean(date) && isOnVacationOn(employee, date);
    const vacationReturnKey = onVacation ? String(employee.vacationReturnDate).slice(0, 10) : null;

    const renderCustomer = () => {
        if (customerMode === 'new') {
            // Bloco destacado: os campos abaixo criam um cadastro de cliente, separado do
            // agendamento, e o fundo deixa isso claro para quem está preenchendo.
            return (
                <div className="collab-quick-booking__new-customer">
                    <div className="collab-quick-booking__new-customer-header">
                        <span>
                            <i className="fa-solid fa-user-plus me-2" aria-hidden="true"></i>
                            Cadastro de cliente novo
                        </span>
                        <Button variant="link" className="collab-quick-booking__link" onClick={() => setCustomerMode('search')}>
                            <i className="fa fa-search me-1"></i>
                            Buscar cadastrado
                        </Button>
                    </div>
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Group>
                                <Form.Label>Nome <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    maxLength={60}
                                    placeholder="Nome completo"
                                    value={newCustomer.name}
                                    onChange={(event) => setNewCustomer({ ...newCustomer, name: event.target.value })}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Telefone <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="tel"
                                    inputMode="tel"
                                    maxLength={20}
                                    placeholder="(00) 00000-0000"
                                    value={newCustomer.phone}
                                    onChange={(event) => setNewCustomer({ ...newCustomer, phone: event.target.value })}
                                    onBlur={() => setNewCustomer((current) => ({ ...current, phone: formatPhoneNumber(current.phone) }))}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>CPF <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={14}
                                    placeholder="000.000.000-00"
                                    value={formatCpf(newCustomer.document)}
                                    onChange={(event) => setNewCustomer({ ...newCustomer, document: event.target.value })}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12}>
                            <Form.Group>
                                <Form.Label>E-mail <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="email"
                                    inputMode="email"
                                    autoComplete="off"
                                    maxLength={50}
                                    placeholder="cliente@exemplo.com"
                                    value={newCustomer.email}
                                    onChange={(event) => setNewCustomer({ ...newCustomer, email: event.target.value })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <p className="collab-quick-booking__new-customer-hint">
                        <i className="fa-solid fa-circle-info me-1" aria-hidden="true"></i>
                        Informe ao menos um: CPF ou e-mail. É por eles que o cliente encontra o horário no site.
                    </p>
                </div>
            );
        }

        if (customer) {
            return (
                <Card className="service-card selected collab-quick-booking__customer">
                    <Card.Body className="d-flex align-items-center p-3">
                        <span className="collab-quick-booking__avatar me-3" aria-hidden="true">{getInitials(customer.name)}</span>
                        <div className="flex-grow-1 min-w-0">
                            <h6 className="service-name mb-0 text-truncate">{customer.name}</h6>
                            {customer.phone && <small className="text-muted">{customer.phone}</small>}
                        </div>
                        <Button variant="link" className="collab-quick-booking__link ms-2" onClick={() => { setCustomer(null); setTerm(''); }}>
                            Trocar
                        </Button>
                    </Card.Body>
                </Card>
            );
        }

        return (
            <>
                <InputGroup className="service-search">
                    <InputGroup.Text className="border-end-0">
                        <i className="fa fa-search text-muted"></i>
                    </InputGroup.Text>
                    <Form.Control
                        type="search"
                        className="border-start-0 ps-0"
                        placeholder="Nome ou telefone do cliente"
                        value={term}
                        onChange={(event) => setTerm(event.target.value)}
                        autoComplete="off"
                    />
                </InputGroup>

                {searching && (
                    <div className="collab-quick-booking__hint"><Spinner animation="border" size="sm" className="me-2" />Buscando...</div>
                )}
                {!searching && term.trim().length > 0 && term.trim().length < 3 && (
                    <div className="collab-quick-booking__hint">Digite ao menos 3 letras ou números.</div>
                )}
                {!searching && results.length > 0 && (
                    <div className="collab-quick-booking__results">
                        {results.map((item) => (
                            <Card
                                key={item.id}
                                className="service-card cursor-pointer"
                                role="button"
                                tabIndex={0}
                                onClick={() => setCustomer(item)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setCustomer(item);
                                    }
                                }}
                            >
                                <Card.Body className="d-flex align-items-center p-3">
                                    <span className="collab-quick-booking__avatar me-3" aria-hidden="true">{getInitials(item.name)}</span>
                                    <div className="flex-grow-1 min-w-0">
                                        <h6 className="service-name mb-0 text-truncate">{item.name}</h6>
                                        {item.phone && <small className="text-muted">{item.phone}</small>}
                                    </div>
                                    <i className="fa fa-chevron-right text-muted ms-2" aria-hidden="true"></i>
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                )}
                {!searching && term.trim().length >= 3 && results.length === 0 && (
                    <div className="collab-quick-booking__hint">Nenhum cliente encontrado.</div>
                )}

                <Button variant="link" className="collab-quick-booking__link" onClick={startNewCustomer}>
                    <i className="fa fa-user-plus me-2"></i>
                    Cadastrar cliente novo
                </Button>
            </>
        );
    };

    return (
        <Modal show={open} onHide={() => !saving && onClose()} centered scrollable dialogClassName="employee-appointment-modal collab-quick-booking-modal">
            <Modal.Header closeButton>
                <Modal.Title>Novo agendamento</Modal.Title>
            </Modal.Header>
            <Modal.Body className="collab-quick-booking">
                {error && !unavailable && (
                    <Alert variant="danger" className="collab-quick-booking__error">
                        <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                        <span>{error}</span>
                    </Alert>
                )}
                {unavailable ? (
                    <div className="collab-quick-booking__unavailable" role="status">
                        <span className="collab-quick-booking__unavailable-icon" aria-hidden="true">
                            <i className="fa-solid fa-calendar-xmark"></i>
                        </span>
                        <h5>Agendamento indisponível</h5>
                        <p>{unavailable}</p>
                    </div>
                ) : loadingOptions ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <>
                        {branches.length > 1 && (
                            <section className="collab-quick-booking__section">
                                <Form.Group>
                                    <Form.Label className="collab-quick-booking__title">
                                        <i className="fa fa-store me-2"></i>Unidade
                                    </Form.Label>
                                    <Form.Select value={branchId} onChange={(event) => setBranchId(event.target.value)}>
                                        <option value="">Escolha a unidade</option>
                                        {branches.map((branch) => (
                                            <option key={branch.id} value={branch.id}>{branch.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </section>
                        )}

                        <section className="collab-quick-booking__section">
                            <h6 className="collab-quick-booking__title">
                                <i className="fa fa-user me-2"></i>Cliente
                            </h6>
                            {renderCustomer()}
                        </section>

                        <section className="collab-quick-booking__section">
                            <h6 className="collab-quick-booking__title">
                                <i className="fa fa-list me-2"></i>Serviços
                            </h6>
                            {!branchId && <div className="collab-quick-booking__hint">Escolha a unidade primeiro.</div>}
                            {branchId && services.length === 0 && (
                                <div className="collab-quick-booking__hint">Nenhum serviço disponível para você nesta unidade.</div>
                            )}
                            <div className="collab-quick-booking__services">
                                {services.map((service) => {
                                    const checked = serviceIds.includes(service.id);
                                    return (
                                        <Card
                                            key={service.id}
                                            className={`service-card cursor-pointer ${checked ? 'selected' : ''}`}
                                            onClick={() => toggleService(service.id)}
                                            role="checkbox"
                                            aria-checked={checked}
                                            tabIndex={0}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    toggleService(service.id);
                                                }
                                            }}
                                        >
                                            <Card.Body className="d-flex align-items-center p-3">
                                                <span className="service-check me-3" aria-hidden="true">
                                                    <i className="fa fa-check"></i>
                                                </span>
                                                <div className="service-info flex-grow-1 min-w-0">
                                                    <h6 className="service-name mb-1">{service.name}</h6>
                                                    <div className="service-details d-flex gap-3">
                                                        <span className="text-muted small">
                                                            <i className="fa fa-clock me-1"></i>
                                                            {service.duration || 30} min
                                                        </span>
                                                        {service.price != null && (
                                                            <span className="collab-quick-booking__price fw-semibold small">
                                                                {service.priceFrom ? 'A partir de ' : ''}
                                                                {currencyFormatter.format(service.price)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    );
                                })}
                            </div>
                            {selectedServices.length > 0 && (
                                <div className="collab-quick-booking__summary">
                                    <span>
                                        {selectedServices.length} {selectedServices.length === 1 ? 'serviço' : 'serviços'} · {totalDuration} min
                                    </span>
                                    <strong>{currencyFormatter.format(totalPrice)}</strong>
                                </div>
                            )}
                        </section>

                        <section className="collab-quick-booking__section">
                            <h6 className="collab-quick-booking__title">
                                <i className="fa fa-calendar-alt me-2"></i>Dia e horário
                            </h6>
                            {/* O dia abre no atual (ou no clicado na grade). O horário não é digitado:
                                sai dos horários livres abaixo, os únicos que o servidor aceitaria. */}
                            <Row className="g-3">
                                <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Label>Dia</Form.Label>
                                        <Form.Control
                                            type="date"
                                            min={todayKey}
                                            value={date}
                                            onChange={(event) => event.target.value && setDate(event.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {!branchId && <div className="collab-quick-booking__hint">Escolha a unidade para ver os horários.</div>}
                            {branchId && slotsLoading && (
                                <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                            )}
                            {onVacation && (
                                <div className="collab-quick-booking__vacation" role="status">
                                    <i className="fa-solid fa-umbrella-beach" aria-hidden="true"></i>
                                    <div>
                                        <strong className="d-block">Você está de férias neste dia</strong>
                                        <span>
                                            Férias de {vacationPeriodLabel(employee)}. Você volta a atender em {vacationReturnLabel(employee)}.
                                        </span>
                                        <Button variant="link" className="collab-quick-booking__link d-block mt-1" onClick={() => setDate(vacationReturnKey)}>
                                            Ver horários a partir de {vacationReturnLabel(employee)}
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {branchId && !slotsLoading && !onVacation && periods.length === 0 && (
                                <div className="collab-quick-booking__hint">
                                    <i className="fa fa-calendar-times me-2"></i>
                                    Nenhum horário livre neste dia. Escolha outro dia.
                                </div>
                            )}
                            {branchId && !slotsLoading && periods.map((period) => (
                                <div key={period.key} className="time-period mb-3">
                                    <h6 className="period-title text-muted mb-2">
                                        <i className={`fa ${period.icon} me-2`}></i>
                                        {period.label}
                                    </h6>
                                    <div className="time-slots d-flex flex-wrap gap-2">
                                        {period.times.map((item) => (
                                            <Button
                                                key={item}
                                                variant={item === time ? 'primary' : 'outline-primary'}
                                                className="time-slot-btn"
                                                onClick={() => setTime(item)}
                                                aria-pressed={item === time}
                                            >
                                                {item}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {/* Resumo do que foi escolhido: deixa o dia e o horário explícitos antes
                                de agendar, já que o horário não fica num campo. */}
                            {branchId && !onVacation && (
                                <div className={`collab-quick-booking__selection${time ? ' is-selected' : ''}`} aria-live="polite">
                                    <i className={time ? 'fa-solid fa-calendar-check' : 'fa-regular fa-clock'} aria-hidden="true"></i>
                                    <span>{time ? formatSelection(date, time) : 'Escolha um horário'}</span>
                                </div>
                            )}
                        </section>

                        <section className="collab-quick-booking__section">
                            <Form.Group>
                                <Form.Label className="collab-quick-booking__title">
                                    <i className="fa fa-comment me-2"></i>Observação <span className="text-muted fw-normal">(opcional)</span>
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    maxLength={500}
                                    value={notes}
                                    onChange={(event) => setNotes(event.target.value)}
                                />
                            </Form.Group>
                        </section>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="employee-appointment-modal-footer">
                {unavailable ? (
                    <Button variant="primary" onClick={onClose} className="collab-portal-btn--primary px-4">
                        Entendi
                    </Button>
                ) : (
                    <>
                        <Button variant="secondary" onClick={onClose} disabled={saving} className="employee-appointment-modal-close-btn px-4">
                            Cancelar
                        </Button>
                        <Button variant="primary" onClick={submit} disabled={saving || loadingOptions} className="collab-portal-btn--primary px-4">
                            {saving ? <><Spinner size="sm" className="me-2" />Agendando...</> : 'Agendar'}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default CollaboratorQuickBooking;
